/* eslint-env es2020 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table, Button, Space, Modal, Form, Input, Select, message, Tag,
  Descriptions, InputNumber, Card, Row, Col, Divider, List, Typography, Checkbox, Image
} from 'antd';
import { EyeOutlined, EditOutlined, ShoppingCartOutlined, DollarOutlined, CloseOutlined, CustomerServiceOutlined, PlusOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { orderAPI, productAPI, freightAPI, customizationAPI, shippingCompanyAPI } from '../api';
import { normalizeMoney, moneyToCents, centsToMoney } from '../utils/money';
import { useNavigate } from 'react-router-dom';
import countryList from 'react-select-country-list';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

const toQuantity = (value) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const sumItemDetailsCents = (items = []) =>
  items.reduce((total, item) => {
    const unitPrice = normalizeMoney(item?.unit_price ?? '0');
    const quantity = toQuantity(item?.quantity ?? 0);
    return total + moneyToCents(unitPrice) * BigInt(quantity);
  }, 0n);

const sumPriceDetailsCents = (details = []) =>
  details.reduce((total, detail) => {
    const amount = normalizeMoney(detail?.amount ?? '0');
    return total + moneyToCents(amount);
  }, 0n);

const multiplyMoneyByQuantity = (value, quantity) =>
  centsToMoney(moneyToCents(normalizeMoney(value ?? '0')) * BigInt(toQuantity(quantity)));

// 获取API基础URL
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

const OrderManagement = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // 分页相关状态
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [priceModalVisible, setPriceModalVisible] = useState(false);
  const [fulfillmentModalVisible, setFulfillmentModalVisible] = useState(false);
  const [rmaModalVisible, setRmaModalVisible] = useState(false);
  const [rmaUpdateModalVisible, setRmaUpdateModalVisible] = useState(false);
  const [messageModalVisible, setMessageModalVisible] = useState(false);
  const [warningModalVisible, setWarningModalVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [form] = Form.useForm();
  const [priceForm] = Form.useForm();
  const [fulfillmentForm] = Form.useForm();
  const [rmaForm] = Form.useForm();
  const [rmaUpdateForm] = Form.useForm();
  const [messageForm] = Form.useForm();
  const [products, setProducts] = useState({}); // 缓存产品信息
  const [batchFulfillmentStatus, setBatchFulfillmentStatus] = useState('');
  const [batchTrackingNumber, setBatchTrackingNumber] = useState('');
  const [batchShippingCompanyId, setBatchShippingCompanyId] = useState(null);
  const [priceSummary, setPriceSummary] = useState({
    itemsTotal: '0.00',
    priceDetailsTotal: '0.00',
    orderTotal: '0.00',
  });
  const [modal, modalContextHolder] = Modal.useModal();
  const [freightChannels, setFreightChannels] = useState([]);
  const [customizations, setCustomizations] = useState([]);
  const [shippingCompanies, setShippingCompanies] = useState([]);
  const countryListInstance = useMemo(() => countryList(), []);

  // 计算总金额并更新价格摘要
  const calculateTotal = useCallback(() => {
    const itemsDetail = priceForm.getFieldValue('items_detail') || [];
    const priceDetails = priceForm.getFieldValue('price_details') || [];

    const itemsTotalCents = sumItemDetailsCents(itemsDetail);
    const priceDetailsTotalCents = sumPriceDetailsCents(priceDetails);
    const totalCents = itemsTotalCents + priceDetailsTotalCents;

    setPriceSummary({
      itemsTotal: centsToMoney(itemsTotalCents),
      priceDetailsTotal: centsToMoney(priceDetailsTotalCents),
      orderTotal: centsToMoney(totalCents),
    });
  }, [priceForm]);

  const fetchOrders = useCallback(async (
    page = pagination.current,
    pageSize = pagination.pageSize,
    searchValue = searchTerm
  ) => {
    try {
      setLoading(true);
      const trimmedSearch = searchValue?.trim();
      const params = { page, page_size: pageSize };
      if (trimmedSearch) {
        params.search = trimmedSearch;
      }
      const response = await orderAPI.getAll(params);
      
      // 处理分页响应
      const ordersData = response.data.data || [];
      const normalizedOrders = ordersData.map(order => ({
        ...order,
        total_amount: order.total_amount != null ? normalizeMoney(order.total_amount) : '0.00',
        price_details: (order.price_details || []).map(detail => ({
          ...detail,
          amount: detail?.amount != null ? normalizeMoney(detail.amount) : '0.00',
        })),
        items_detail: (order.items_detail || []).map(item => ({
          ...item,
          unit_price: item?.unit_price != null ? normalizeMoney(item.unit_price) : '0.00',
          customization_cost: item?.customization_cost != null ? normalizeMoney(item.customization_cost) : '0.00',
        })),
      }));
      setOrders(normalizedOrders);
      setPagination(prev => ({
        ...prev,
        current: response.data.page,
        pageSize: response.data.page_size,
        total: response.data.total,
      }));

      // 获取所有订单中涉及的产品信息
      const productIds = new Set();
      ordersData.forEach(order => {
        if (order.items_detail) {
          order.items_detail.forEach(item => {
            productIds.add(item.product_id);
          });
        }
      });

      // 批量获取产品信息
      const productPromises = Array.from(productIds).map(async (productId) => {
        try {
          const productResponse = await productAPI.getById(productId, { include_variant_images: true });
          return { id: productId, data: productResponse.data };
        } catch (error) {
          
          return { id: productId, data: null };
        }
      });

      const productResults = await Promise.all(productPromises);
      const productMap = {};
      productResults.forEach(result => {
        productMap[result.id] = result.data;
      });
      setProducts(productMap);

    } catch (error) {
      message.error('获取订单列表失败');
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, searchTerm]);

  useEffect(() => {
    fetchOrders();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchFreightChannels = useCallback(async () => {
    try {
      const response = await freightAPI.getAll();
      setFreightChannels(response.data || []);
    } catch (error) {
      
    }
  }, []);

  const fetchCustomizations = useCallback(async () => {
    try {
      const response = await customizationAPI.getAll({ limit: 500 });
      setCustomizations(response.data || []);
    } catch (error) {
      
    }
  }, []);

  const fetchShippingCompanies = useCallback(async () => {
    try {
      const response = await shippingCompanyAPI.getAll();
      setShippingCompanies(response.data || []);
    } catch (error) {
      
    }
  }, []);

  useEffect(() => {
    fetchFreightChannels();
    fetchCustomizations();
    fetchShippingCompanies();
  }, [fetchFreightChannels, fetchCustomizations, fetchShippingCompanies]);

  const freightChannelMap = useMemo(() => {
    const map = {};
    freightChannels.forEach((channel) => {
      map[channel.id] = channel;
    });
    return map;
  }, [freightChannels]);

  const customizationMap = useMemo(() => {
    const map = {};
    customizations.forEach((customization) => {
      map[customization.id] = customization;
    });
    return map;
  }, [customizations]);

  const shippingCompanyMap = useMemo(() => {
    const map = {};
    shippingCompanies.forEach((company) => {
      map[company.id] = company;
    });
    return map;
  }, [shippingCompanies]);

  const formatCountry = useCallback((code, emptyText) => {
    if (!code) {
      return emptyText;
    }
    const normalized = (code || '').toUpperCase();
    const label = countryListInstance.getLabel(normalized) || '';
    return label ? `${normalized} (${label})` : normalized;
  }, [countryListInstance]);

  const handleSearchInputChange = (event) => {
    const value = event.target.value ?? '';
    setSearchTerm(value);
    if (value === '') {
      setPagination(prev => ({
        ...prev,
        current: 1,
      }));
      fetchOrders(1, pagination.pageSize, '');
    }
  };

  const handleSearchOrders = (value) => {
    const normalizedValue = value ?? '';
    setSearchTerm(normalizedValue);
    setPagination(prev => ({
      ...prev,
      current: 1,
    }));
    fetchOrders(1, pagination.pageSize, normalizedValue);
  };

  // 处理分页、排序、筛选变化
  const handleTableChange = (paginationConfig, filters, sorter) => {
    const { current, pageSize } = paginationConfig;
    fetchOrders(current, pageSize, searchTerm);
  };

  const handleViewDetail = (record) => {
    setSelectedOrder(record);
    setDetailModalVisible(true);
  };

  const handleEditStatus = (record) => {
    setSelectedOrder(record);
    form.setFieldsValue({ status: record.status });
    setEditModalVisible(true);
  };

  const handleUpdateStatus = async (values) => {
    try {
      await orderAPI.update(selectedOrder.order_number, values);
      message.success('状态更新成功');
      setEditModalVisible(false);
      fetchOrders();
    } catch (error) {
      message.error('状态更新失败');
    }
  };

  const handleCancelOrder = async (orderNumber) => {
    try {
      await orderAPI.cancel(orderNumber);
      message.success('订单已取消');
      fetchOrders();
    } catch (error) {
      message.error('取消订单失败');
    }
  };

  const handleMarkAsPaid = async (orderNumber) => {
    try {
      const response = await orderAPI.pay(orderNumber);
      
      // 检查支付结果
      if (response.data.failed_count > 0) {
        const error = response.data.failed_orders[0].error;
        message.error(`付款失败: ${error}`);
      } else {
        message.success('订单已付款');
      }
      fetchOrders();
    } catch (error) {
      if (error.response?.data?.detail) {
        message.error(`付款失败: ${error.response.data.detail}`);
      } else {
        message.error('付款失败');
      }
    }
  };

  // 价格管理
  const handleEditPrice = (record) => {
    setSelectedOrder(record);

    // 过滤掉"Item price"项，因为它应该由商品价格自动计算
    const filteredPriceDetails = (record.price_details || []).filter(
      detail => detail.name !== 'Item price'
    ).map(detail => ({
      ...detail,
      amount: normalizeMoney(detail.amount ?? '0'),
    }));

    // 初始化表单数据
    const initialData = {
      total_amount: normalizeMoney(record.total_amount ?? '0'),
      price_details: filteredPriceDetails,
      items_detail: record.items_detail?.map(item => ({
        ...item,
        unit_price: normalizeMoney(item.unit_price ?? '0')
      })) || []
    };

    priceForm.setFieldsValue(initialData);
    setPriceModalVisible(true);
    setTimeout(calculateTotal, 0);
  };

  const handleUpdatePrice = async (values) => {
    try {
      const itemsDetail = values.items_detail || [];
      const priceDetails = values.price_details || [];

      const itemsTotalCents = sumItemDetailsCents(itemsDetail);
      const priceDetailsTotalCents = sumPriceDetailsCents(priceDetails);

      const processedPriceDetails = priceDetails.map(detail => ({
        ...detail,
        amount: normalizeMoney(detail?.amount ?? '0'),
      }));

      const finalPriceDetails = [
        {
          name: 'Item price',
          amount: centsToMoney(itemsTotalCents),
          unit: 'USD',
        },
        ...processedPriceDetails,
      ];

      const updateData = {
        ...values,
        items_detail: itemsDetail.map(item => ({
          ...item,
          unit_price: normalizeMoney(item?.unit_price ?? '0'),
        })),
        price_details: finalPriceDetails,
        total_amount: centsToMoney(itemsTotalCents + priceDetailsTotalCents),
      };

      await orderAPI.updatePrice(selectedOrder.order_number, updateData);
      message.success('价格更新成功');
      setPriceModalVisible(false);
      fetchOrders();
    } catch (error) {
      message.error('价格更新失败');
    }
  };

  // Fulfillment管理
  const handleManageFulfillment = (record) => {
    setSelectedOrder(record);

    // 重置批量操作状态
    setBatchFulfillmentStatus('');
    setBatchTrackingNumber('');
    setBatchShippingCompanyId(null);

    // 初始化fulfillment表单
    const initialItems = record.items_detail?.map((item, index) => ({
      item_index: index,
      fulfillment_status: item.fulfillment_status || 'AWAITING',
      comment: item.comment || '',
      tracking_number: item.tracking_number || '',
      shipping_company_id: item.shipping_company_id || null,
      tracking_url: item.tracking_url || ''
    })) || [];

    fulfillmentForm.setFieldsValue({ items: initialItems });
    setFulfillmentModalVisible(true);
  };

  // 批量更新所有商品状态
  const handleBatchStatusChange = (status) => {
    setBatchFulfillmentStatus(status);
    if (status) {
      const currentItems = fulfillmentForm.getFieldValue('items') || [];
      const updatedItems = currentItems.map(item => ({
        ...item,
        fulfillment_status: status
      }));
      fulfillmentForm.setFieldsValue({ items: updatedItems });
    }
  };

  // 批量更新物流公司
  const handleBatchShippingCompanyChange = (shippingCompanyId) => {
    setBatchShippingCompanyId(shippingCompanyId ?? null);
    const currentItems = fulfillmentForm.getFieldValue('items') || [];
    const updatedItems = currentItems.map(item => ({
      ...item,
      shipping_company_id: shippingCompanyId ?? null
    }));
    fulfillmentForm.setFieldsValue({ items: updatedItems });
  };

  // 批量更新所有商品跟踪号
  const handleBatchTrackingChange = (trackingNumber) => {
    setBatchTrackingNumber(trackingNumber);
    if (trackingNumber) {
      const currentItems = fulfillmentForm.getFieldValue('items') || [];
      const updatedItems = currentItems.map(item => ({
        ...item,
        tracking_number: trackingNumber
      }));
      fulfillmentForm.setFieldsValue({ items: updatedItems });
    }
  };

  const handleGenerateTrackingUrl = (itemIdx) => {
    const items = fulfillmentForm.getFieldValue('items') || [];
    const currentItem = items[itemIdx] || {};
    const trackingNumber = (currentItem?.tracking_number || '').trim();
    const shippingCompanyId = currentItem?.shipping_company_id;

    if (!shippingCompanies.length) {
      message.error('请先加载物流公司列表或检查接口权限');
      return;
    }

    if (!trackingNumber) {
      window.alert('请先填写跟踪号');
      return;
    }
    if (!shippingCompanyId) {
      window.alert('请先选择物流公司');
      return;
    }

    const company =
      shippingCompanyMap[shippingCompanyId] ||
      shippingCompanyMap[Number(shippingCompanyId)] ||
      shippingCompanies.find((c) => `${c.id}` === `${shippingCompanyId}`);
    if (!company) {
      message.error('未找到所选物流公司');
      return;
    }
    if (!company?.tracking_url_template) {
      window.alert('所选物流公司未设置URL模板');
      return;
    }

    const generatedUrl = company.tracking_url_template.replace(/__TRACKING__NUMBER__/g, trackingNumber);
    const nextItems = [...items];
    nextItems[itemIdx] = {
      ...currentItem,
      tracking_url: generatedUrl,
    };
    fulfillmentForm.setFieldsValue({ items: nextItems });
    message.success('已生成跟踪URL');
  };

  // 批量生成所有商品的跟踪URL
  const handleBatchGenerateTrackingUrls = () => {
    const items = fulfillmentForm.getFieldValue('items') || [];

    if (!shippingCompanies.length) {
      message.error('请先加载物流公司列表或检查接口权限');
      return;
    }

    let updatedCount = 0;
    const updatedItems = items.map((item) => {
      const trackingNumber = (item?.tracking_number || '').trim();
      const shippingCompanyId = item?.shipping_company_id;
      const company =
        shippingCompanyMap[shippingCompanyId] ||
        shippingCompanyMap[Number(shippingCompanyId)] ||
        shippingCompanies.find((c) => `${c.id}` === `${shippingCompanyId}`);

      if (!trackingNumber || !shippingCompanyId || !company?.tracking_url_template) {
        return item;
      }

      updatedCount += 1;
      return {
        ...item,
        tracking_url: company.tracking_url_template.replace(/__TRACKING__NUMBER__/g, trackingNumber),
      };
    });

    if (updatedCount === 0) {
      message.warning('请确保所有商品已填写物流公司和跟踪号，并已配置URL模板');
      return;
    }

    fulfillmentForm.setFieldsValue({ items: updatedItems });
    message.success(`已为 ${updatedCount} 个商品生成跟踪URL`);
  };

  const handleUpdateFulfillment = async (values) => {
    try {
      await orderAPI.updateFulfillment(selectedOrder.order_number, values.items);
      message.success('订单状态更新成功');
      setFulfillmentModalVisible(false);
      fetchOrders();
    } catch (error) {
      message.error('订单状态更新失败');
    }
  };

  // RMA管理
  const handleInitiateRMA = (record) => {
    setSelectedOrder(record);

    // 初始化RMA表单，所有商品都可以申请售后（包括未发货的）
    const eligibleItems = record.items_detail?.map((item, index) => {
      // 只要不是已经在RMA流程中的商品都可以申请
      if (!item.rma_status) {
        return {
          item_index: index,
          rma_comment: '',
          selected: false,
          product_id: item.product_id,
          variant_path: item.variant_path,
          quantity: item.quantity,
          fulfillment_status: item.fulfillment_status
        };
      }
      return null;
    }).filter(Boolean) || [];

    if (eligibleItems.length === 0) {
      message.warning('所有商品都已在售后流程中');
      return;
    }

    rmaForm.setFieldsValue({ items: eligibleItems });
    setRmaModalVisible(true);
  };

  const handleSubmitRMA = async (values) => {
    try {
      const selectedItems = values.items.filter(item => item.selected);
      await orderAPI.requestRMA(selectedOrder.order_number, selectedItems);
      message.success('RMA申请提交成功');
      setRmaModalVisible(false);
      fetchOrders();
    } catch (error) {
      message.error('RMA申请失败');
    }
  };

  const handleUpdateRMA = (record) => {
    setSelectedOrder(record);

    // 初始化RMA更新表单
    const rmaItems = record.items_detail?.map((item, index) => {
      if (item.rma_status) {
        return {
          item_index: index,
          rma_status: item.rma_status,
          rma_shipment: item.rma_shipment || ''
        };
      }
      return null;
    }).filter(Boolean) || [];

    rmaUpdateForm.setFieldsValue({ items: rmaItems });
    setRmaUpdateModalVisible(true);
  };

  const handleSubmitRMAUpdate = async (values) => {
    try {
      await orderAPI.updateRMA(selectedOrder.order_number, values.items);
      message.success('RMA状态更新成功');
      setRmaUpdateModalVisible(false);
      fetchOrders();
    } catch (error) {
      message.error('RMA状态更新失败');
    }
  };

  // 编辑订单备注
  const handleEditMessage = (record) => {
    setSelectedOrder(record);
    messageForm.setFieldsValue({ message: record.message || '' });
    setMessageModalVisible(true);
  };

  const handleUpdateMessage = async (values) => {
    try {
      const updatedOrder = await orderAPI.updateMessage(selectedOrder.order_number, values.message);
      message.success('订单备注更新成功');
      setMessageModalVisible(false);

      // 更新selectedOrder状态以立即反映变化
      setSelectedOrder({
        ...selectedOrder,
        message: values.message
      });

      fetchOrders();
    } catch (error) {
      if (error.response?.data?.detail) {
        message.error(error.response.data.detail);
      } else {
        message.error('订单备注更新失败');
      }
    }
  };

  const handleDeleteOrder = async (orderNumber) => {
    try {
      await orderAPI.delete(orderNumber);
      message.success('订单删除成功');
      fetchOrders();
    } catch (error) {
      if (error.response?.data?.detail) {
        message.error(error.response.data.detail);
      } else {
        message.error('订单删除失败');
      }
    }
  };

  // 显示警告信息
  const handleShowWarning = (record) => {
    setSelectedOrder(record);
    setWarningModalVisible(true);
  };

  // 清除警告
  const handleClearWarning = async () => {
    try {
      await orderAPI.clearWarning(selectedOrder.order_number);
      message.success('警告已清除');
      setWarningModalVisible(false);

      // 更新selectedOrder状态以立即反映变化
      setSelectedOrder({
        ...selectedOrder,
        warning: []
      });

      fetchOrders();
    } catch (error) {
      if (error.response?.data?.detail) {
        message.error(error.response.data.detail);
      } else {
        message.error('清除警告失败');
      }
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'AWAITING_PAYMENT': 'orange',
      'CONFIRMED': 'blue',
      'CANCELLED': 'red',
      'CLOSED': 'green',
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status) => {
    const texts = {
      'AWAITING_PAYMENT': '未付款',
      'CONFIRMED': '已付款',
      'CANCELLED': '已取消',
      'CLOSED': '已完成',
    };
    return texts[status] || status;
  };

  const getFulfillmentStatusColor = (status) => {
    const colors = {
      'AWAITING': 'orange',
      'ACCEPTED': 'blue',
      'REJECTED': 'red',
      'ON_HOLD': 'yellow',
      'SHIPPED': 'green',
      'DELIVERED': 'cyan',
    };
    return colors[status] || 'default';
  };

  const getFulfillmentStatusText = (status) => {
    const texts = {
      'AWAITING': '等待确认',
      'ACCEPTED': '已确认',
      'REJECTED': '拒单',
      'ON_HOLD': '缺货',
      'SHIPPED': '已发货',
      'DELIVERED': '已送达',
    };
    return texts[status] || status;
  };

  const getRMAStatusColor = (status) => {
    const colors = {
      'RMA_REQUESTED': 'orange',
      'RMA_AUTHORIZED': 'blue',
      'RMA_IN_TRANSIT': 'yellow',
      'RMA_RECEIVED': 'green',
      'REFUND_COMPLETED': 'cyan',
    };
    return colors[status] || 'default';
  };

  const getRMAStatusText = (status) => {
    const texts = {
      'RMA_REQUESTED': '已申请售后',
      'RMA_AUTHORIZED': '已同意售后',
      'RMA_IN_TRANSIT': '商品已寄回',
      'RMA_RECEIVED': '已收到寄回商品',
      'REFUND_COMPLETED': '售后完毕',
    };
    return texts[status] || status;
  };

  const normalizeVariantPath = (variantPath) => {
    if (Array.isArray(variantPath)) {
      return variantPath;
    }
    if (typeof variantPath === 'string') {
      return variantPath.split('.').filter(Boolean);
    }
    return [];
  };

  const variantPathsEqual = (pathA, pathB) => {
    const a = normalizeVariantPath(pathA);
    const b = normalizeVariantPath(pathB);
    if (a.length !== b.length) return false;
    return a.every((segment, index) => segment === b[index]);
  };

  const formatVariantPath = (variantPath) => {
    const normalized = normalizeVariantPath(variantPath);
    if (!normalized.length) {
      return '';
    }
    return normalized.join(' - ');
  };

  // 获取变体信息的辅助函数
  const getVariantInfo = (variants, variantPath) => {
    if (!variants || !Array.isArray(variants)) return null;

    const targetPath = normalizeVariantPath(variantPath);

    if (targetPath.length === 0) {
      return variants.find(variant => !variant.variant_path || variant.variant_path.length === 0) || null;
    }

    return variants.find(variant => variantPathsEqual(variant.variant_path || [], targetPath)) || null;
  };

  // 获取产品封面图片URL
  const getProductCoverImage = (product) => {
    if (!product?.images?.length) {
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjEwIiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+Tm8gSW1hZ2U8L3RleHQ+PC9zdmc+';
    }

    // 优先显示非变体图片作为封面
    const coverImage = product.images.find(img => !img.for_variants) || product.images[0];
    return `${API_BASE_URL}/products/${product.id}/images/${coverImage.id}`;
  };

  // 获取变体图片URL
  const getVariantImage = (product, variantPath) => {
    const path = normalizeVariantPath(variantPath);
    if (!product?.images?.length || path.length === 0) return null;

    const variantInfo = getVariantInfo(product.variants, path);
    if (!variantInfo?.image) return null;

    return `${API_BASE_URL}/products/${product.id}/images/${variantInfo.image}`;
  };

  // 处理产品点击跳转
  const handleProductClick = (productId) => {
    navigate(`/product/${productId}`);
  };

  // 获取订单整体fulfillment状态摘要
  const getFulfillmentSummary = (items_detail, warning, record) => {
    if (!items_detail || items_detail.length === 0) return '无商品';

    const statusCounts = {};
    let hasAbnormal = false;

    items_detail.forEach(item => {
      const status = item.fulfillment_status || 'AWAITING';
      statusCounts[status] = (statusCounts[status] || 0) + 1;

      if (['ON_HOLD', 'REJECTED'].includes(status)) {
        hasAbnormal = true;
      }
    });

    let statusDisplay;

    // 如果所有商品状态相同
    const statuses = Object.keys(statusCounts);
    if (statuses.length === 1) {
      const status = statuses[0];
      if (hasAbnormal) {
        statusDisplay = <Tag color="red">Abnormal</Tag>;
      } else {
        statusDisplay = <Tag color={getFulfillmentStatusColor(status)}>{getFulfillmentStatusText(status)}</Tag>;
      }
    } else if (hasAbnormal) {
      // 如果有异常状态，优先显示
      statusDisplay = <Tag color="red">Abnormal</Tag>;
    } else {
      // 显示状态计数
      statusDisplay = (
        <div>
          {Object.entries(statusCounts).map(([status, count]) => (
            <div key={status} style={{ fontSize: '12px' }}>
              {count}: {getFulfillmentStatusText(status)}
            </div>
          ))}
        </div>
      );
    }

    // 检查是否有警告信息
    const hasWarnings = warning && Array.isArray(warning) && warning.length > 0;

    return (
      <div>
        {statusDisplay}
        {hasWarnings && (
          <div style={{ marginTop: '4px' }}>
            <Button
              size="small"
              type="primary"
              danger
              onClick={() => handleShowWarning(record)}
              style={{
                backgroundColor: '#faad14',
                borderColor: '#faad14',
                fontSize: '10px',
                height: '20px',
                padding: '0 6px'
              }}
            >
              ⚠️ 警告 ({warning.length}) ⚠️
            </Button>
          </div>
        )}
      </div>
    );
  };

  const columns = [
    {
      title: '客户姓名',
      dataIndex: 'customer_name',
      key: 'customer_name',
      width: '12%',
      ellipsis: true,
    },
    {
      title: '商品概览',
      key: 'products',
      width: '25%',
      render: (_, record) => {
        if (record.items_detail && record.items_detail.length > 0) {
          const firstItem = record.items_detail[0];
          const product = products[firstItem.product_id];

          if (!product) {
            return `商品ID: ${firstItem.product_id}`;
          }

          const coverImageUrl = getProductCoverImage(product);
          const variantImageUrl = getVariantImage(product, firstItem.variant_path);

          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {/* 优先显示变体图片，如果没有变体图片则显示封面图片 */}
              <Image
                src={variantImageUrl || coverImageUrl}
                alt={variantImageUrl ? "Variant image" : "Product cover"}
                width={40}
                height={40}
                style={{
                  objectFit: 'cover',
                  borderRadius: '4px',
                  border: variantImageUrl ? '2px solid #ff6b35' : 'none',
                  cursor: 'pointer'
                }}
                onClick={() => handleProductClick(firstItem.product_id)}
                onError={(e) => {
                  // 如果变体图片加载失败，fallback到封面图片
                  if (variantImageUrl && e.target.src === variantImageUrl) {
                    e.target.src = coverImageUrl;
                    e.target.style.border = 'none';
                  } else {
                    // 如果封面图片也失败，显示占位符
                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjgiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5OL0E8L3RleHQ+PC9zdmc+';
                    e.target.style.border = 'none';
                  }
                }}
              />

              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    color: '#1890ff',
                    textDecoration: 'none'
                  }}
                  onClick={() => handleProductClick(firstItem.product_id)}
                  onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                  onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                >
                  {product.name}
                </div>
                {record.items_detail.length > 1 && (
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    等{record.items_detail.length}件商品
                  </div>
                )}
              </div>
            </div>
          );
        }
        return '-';
      },
    },
    {
      title: '订单金额',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (amount) => `$${normalizeMoney(amount ?? '0')}`,
      width: '10%',
    },
    {
      title: '所属用户',
      key: 'owner',
      width: '12%',
      render: (_, record) => {
        if (record.dropshipper && record.dropshipper_id) {
          const user = record.dropshipper;
          const roleColor = user.role === 'admin' ? '#ff4d4f' : '#1890ff';
          return (
            <div>
              <div style={{ fontWeight: 'bold' }}>{user.username}</div>
              <div style={{ fontSize: '11px', color: roleColor }}>
                [{user.role === 'admin' ? '管理员' : 'Dropshipper'}]
              </div>
            </div>
          );
        } else if (record.dropshipper_id) {
          return `用户ID: ${record.dropshipper_id}`;
        } else {
          return <span style={{ color: '#999' }}>无归属</span>;
        }
      },
    },
    {
      title: '渠道',
      dataIndex: 'channel',
      key: 'channel',
      width: '8%',
      render: (channel) => {
        const channelColors = {
          'manual': 'blue',
          'from_admin': 'orange',
          'api': 'green',
          'website': 'purple'
        };
        return (
          <Tag color={channelColors[channel] || 'default'}>
            {channel || 'unknown'}
          </Tag>
        );
      },
    },
    {
      title: '订单状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
      width: '10%',
    },
    {
      title: '订单状态',
      key: 'fulfillment_status',
      render: (_, record) => getFulfillmentSummary(record.items_detail, record.warning, record),
      width: '12%',
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleString('zh-CN'),
      width: '11%',
    },
    {
      title: '操作',
      key: 'action',
      width: '20%',
      render: (_, record) => (
        <Space size="small" wrap>
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            详情
          </Button>

          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => navigate(`/admin/orders/edit/${record.order_number}`)}
            style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', color: 'white' }}
          >
            编辑
          </Button>

          {record.status === 'AWAITING_PAYMENT' && (
            <>
              <Button
                size="small"
                icon={<DollarOutlined />}
                onClick={() => handleEditPrice(record)}
              >
                修改价格
              </Button>
              <Button
                size="small"
                type="primary"
                style={{ backgroundColor: '#ff4d4f', borderColor: '#ff4d4f' }}
                onClick={() => {
                  modal.confirm({
                    title: '确认付款',
                    content: '确定要从用户的余额中扣除订单金额以付款吗？',
                    onOk: () => handleMarkAsPaid(record.order_number),
                  });
                }}
              >
                付款
              </Button>
              <Button
                danger
                size="small"
                icon={<CloseOutlined />}
                onClick={() => {
                  modal.confirm({
                    title: '确认取消订单',
                    content: '取消后无法恢复，确定要取消这个订单吗？',
                    onOk: () => handleCancelOrder(record.order_number),
                  });
                }}
              >
                取消
              </Button>
            </>
          )}

          {record.status === 'CONFIRMED' && (
            <>
              <Button
                size="small"
                type="default"
                onClick={() => handleManageFulfillment(record)}
              >
                编辑状态
              </Button>
              <Button
                size="small"
                icon={<CustomerServiceOutlined />}
                onClick={() => handleInitiateRMA(record)}
              >
                售后
              </Button>
            </>
          )}

          {/* 显示RMA更新按钮，如果有商品在RMA流程中 */}
          {record.items_detail?.some(item => item.rma_status) && (
            <Button
              size="small"
              type="dashed"
              onClick={() => handleUpdateRMA(record)}
            >
              RMA状态
            </Button>
          )}

          {/* 管理员可以强制删除任何状态的订单 */}
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => {
              modal.confirm({
                title: '确认删除订单',
                content: (
                  <div>
                    <p>确定要<strong>永久删除</strong>这个订单吗？</p>
                    <p style={{ color: '#ff4d4f', fontSize: '12px' }}>
                      ⚠️ 删除后无法恢复，如果订单未取消状态，库存将自动恢复
                    </p>
                    <p style={{ fontSize: '12px', color: '#666' }}>
                      订单号：{record.order_number}
                    </p>
                  </div>
                ),
                okText: '确认删除',
                okType: 'danger',
                cancelText: '取消',
                onOk: () => handleDeleteOrder(record.order_number),
              });
            }}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      {modalContextHolder}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 'bold' }}>
          <ShoppingCartOutlined style={{ marginRight: 8 }} />
          订单管理
        </h1>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/admin/orders/create')}
        >
          手动添加订单
        </Button>
      </div>

      <div style={{ marginBottom: 16, maxWidth: 360 }}>
        <Input.Search
          allowClear
          enterButton={<SearchOutlined />}
          placeholder="搜索订单（订单号、客户信息、渠道ID或商品）"
          value={searchTerm}
          onChange={handleSearchInputChange}
          onSearch={handleSearchOrders}
        />
      </div>

      <Table
        columns={columns}
        dataSource={orders}
        rowKey="order_number"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        size="middle"
      />

      {/* Order Details Modal */}
      <Modal
        title="订单详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={1000}
      >
        {selectedOrder && (
          <div>
            <Descriptions title="订单信息" bordered column={2}>
              <Descriptions.Item label="订单号">
                {selectedOrder.order_number}
              </Descriptions.Item>
              <Descriptions.Item label="订单状态">
                <Tag color={getStatusColor(selectedOrder.status)}>
                  {getStatusText(selectedOrder.status)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="客户姓名">
                {selectedOrder.customer_name}
              </Descriptions.Item>
              <Descriptions.Item label="客户邮箱">
                {selectedOrder.customer_email || '未提供'}
              </Descriptions.Item>
              <Descriptions.Item label="客户电话">
                {selectedOrder.customer_phone}
              </Descriptions.Item>
              <Descriptions.Item label="订单金额">
                ${normalizeMoney(selectedOrder.total_amount ?? '0')}
              </Descriptions.Item>
              <Descriptions.Item label="渠道">
                {selectedOrder.channel || 'manual'}
              </Descriptions.Item>
              <Descriptions.Item label="创建时间">
                {new Date(selectedOrder.created_at).toLocaleString('zh-CN')}
              </Descriptions.Item>
              <Descriptions.Item label="国家">
                {formatCountry(selectedOrder.dest_country, '未提供')}
              </Descriptions.Item>
              <Descriptions.Item label="收货地址" span={2}>
                {selectedOrder.shipping_address}
              </Descriptions.Item>
              {(selectedOrder.message || true) && (
                <Descriptions.Item label="订单备注" span={2}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <div style={{ flex: 1, whiteSpace: 'pre-wrap' }}>
                      {selectedOrder.message || (
                        <Text style={{ color: '#999', fontStyle: 'italic' }}>无备注</Text>
                      )}
                    </div>
                    <Button
                      size="small"
                      type="primary"
                      icon={<EditOutlined />}
                      onClick={() => handleEditMessage(selectedOrder)}
                    >
                      编辑备注
                    </Button>
                  </div>
                </Descriptions.Item>
              )}
              {selectedOrder.third_party_info && (
                <Descriptions.Item label="第三方平台信息" span={2}>
                  <div style={{ backgroundColor: '#f5f5f5', padding: '12px', borderRadius: '4px' }}>
                    {Object.entries(selectedOrder.third_party_info).map(([key, value]) => (
                      <div key={key} style={{ marginBottom: '8px' }}>
                        <Text strong style={{ color: '#1890ff' }}>{key}:</Text>
                        <div style={{ marginLeft: '8px', marginTop: '2px' }}>
                          {key.toLowerCase() === 'url' ? (
                            <a href={value} target="_blank" rel="noopener noreferrer" style={{ color: '#1890ff' }}>
                              {value}
                            </a>
                          ) : (
                            <Text>{value}</Text>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </Descriptions.Item>
              )}
            </Descriptions>

            {/* 订单商品详情 */}
            {selectedOrder.items_detail && selectedOrder.items_detail.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <h3>订单商品</h3>
                <List
                  dataSource={selectedOrder.items_detail}
                  renderItem={(item, index) => {
                    const product = products[item.product_id];
                    const coverImageUrl = product ? getProductCoverImage(product) : null;
                    const variantImageUrl = product ? getVariantImage(product, item.variant_path) : null;
                    const freightChannel = item.freight_id ? freightChannelMap[item.freight_id] : null;
                    const customization = item.customization_id ? customizationMap[item.customization_id] : null;
                    const shippingCompany = item.shipping_company_id ? shippingCompanyMap[item.shipping_company_id] : null;

                    return (
                      <List.Item key={index}>
                        <Card size="small" style={{ width: '100%' }}>
                          <Row gutter={16}>
                            <Col span={4}>
                              {/* 商品图片 - 优先显示变体图片，如果没有变体图片则显示封面图片 */}
                              <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <Image
                                  src={variantImageUrl || coverImageUrl}
                                  alt={variantImageUrl ? "Variant image" : "Product cover"}
                                  width={60}
                                  height={60}
                                  style={{
                                    objectFit: 'cover',
                                    borderRadius: '4px',
                                    border: variantImageUrl ? '2px solid #ff6b35' : 'none',
                                    cursor: 'pointer'
                                  }}
                                  onClick={() => handleProductClick(item.product_id)}
                                  onError={(e) => {
                                    // 如果变体图片加载失败，fallback到封面图片
                                    if (variantImageUrl && e.target.src === variantImageUrl) {
                                      e.target.src = coverImageUrl;
                                      e.target.style.border = 'none';
                                    } else {
                                      // 如果封面图片也失败，显示占位符
                                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjEwIiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+Tm8gSW1hZ2U8L3RleHQ+PC9zdmc+';
                                      e.target.style.border = 'none';
                                    }
                                  }}
                                />
                              </div>
                            </Col>
                            <Col span={8}>
                              <div
                                style={{
                                  fontWeight: 'bold',
                                  cursor: 'pointer',
                                  color: '#1890ff',
                                  marginBottom: '4px'
                                }}
                                onClick={() => handleProductClick(item.product_id)}
                                onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                              >
                                {product ? product.name : `商品ID: ${item.product_id}`}
                              </div>
                              {item.variant_path && item.variant_path.length > 0 && (
                                <div style={{ fontSize: '12px', color: '#666' }}>
                                  规格: {item.variant_path.join(' - ')}
                                </div>
                              )}
                              {item.sku && (
                                <div style={{ fontSize: '12px', color: '#1890ff', fontWeight: 'bold' }}>
                                  SKU: {item.sku}
                                </div>
                              )}
                              <div>数量: {item.quantity} | 单价: ${normalizeMoney(item.unit_price ?? '0')}</div>
                              <div>重量: {item.weight}g × {item.quantity} = {item.total_weight}g</div>
                              {item.freight_id && (
                                <div style={{ fontSize: '12px', color: '#666' }}>
                                  Freight Channel: <Text strong>{freightChannel?.name || `ID: ${item.freight_id}`}</Text>
                                </div>
                              )}
                              {item.customization_id && (
                                <div style={{ fontSize: '12px', color: '#666' }}>
                                  Customization: <Text strong>{customization?.customization_name || `ID: ${item.customization_id}`}</Text>
                                </div>
                              )}
                            </Col>
                            <Col span={6}>
                              <div>订单状态:</div>
                              <Tag color={getFulfillmentStatusColor(item.fulfillment_status)}>
                                {getFulfillmentStatusText(item.fulfillment_status)}
                              </Tag>
                              {item.shipping_company_id && (
                                <div style={{ fontSize: '12px', marginTop: 4, color: '#666' }}>
                                  物流公司: <Text strong>{shippingCompany?.name || `ID: ${item.shipping_company_id}`}</Text>
                                </div>
                              )}
                              {item.tracking_number && (
                                <div style={{ fontSize: '12px', marginTop: 4 }}>
                                  跟踪号: {item.tracking_number}
                                </div>
                              )}
                              {item.tracking_url && (
                                <div style={{ fontSize: '12px', marginTop: 4 }}>
                                  跟踪链接:{' '}
                                  <a href={item.tracking_url} target="_blank" rel="noopener noreferrer">
                                    {item.tracking_url}
                                  </a>
                                </div>
                              )}
                              {item.comment && (
                                <div style={{ fontSize: '12px', marginTop: 4, color: '#666' }}>
                                  备注: {item.comment}
                                </div>
                              )}
                            </Col>
                            <Col span={6}>
                              {item.rma_status && (
                                <>
                                  <div>RMA状态:</div>
                                  <Tag color={getRMAStatusColor(item.rma_status)}>
                                    {getRMAStatusText(item.rma_status)}
                                  </Tag>
                                  {item.rma_shipment && (
                                    <div style={{ fontSize: '12px', marginTop: 4 }}>
                                      退货物流: {item.rma_shipment}
                                    </div>
                                  )}
                                  {item.rma_comment && (
                                    <div style={{ fontSize: '12px', marginTop: 4, color: '#666' }}>
                                      RMA备注: {item.rma_comment}
                                    </div>
                                  )}
                                </>
                              )}
                            </Col>
                          </Row>
                        </Card>
                      </List.Item>
                    );
                  }}
                />
              </div>
            )}

            {/* Price Details Section */}
            {selectedOrder.price_details && selectedOrder.price_details.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <h3>价格详情</h3>
                <Table
                  dataSource={selectedOrder.price_details}
                  rowKey="name"
                  pagination={false}
                  size="small"
                  showHeader={false}
                  columns={[
                    {
                      title: '项目',
                      dataIndex: 'name',
                      key: 'name',
                      width: '60%',
                      render: (name) => <strong>{name}</strong>,
                    },
                    {
                      title: '金额',
                      key: 'amount',
                      width: '40%',
                      render: (_, record) => (
                        <div style={{ textAlign: 'right' }}>
                          <strong>${normalizeMoney(record.amount ?? '0')} {record.unit}</strong>
                        </div>
                      ),
                    },
                  ]}
                  summary={() => (
                    <Table.Summary.Row style={{ backgroundColor: '#f5f5f5' }}>
                      <Table.Summary.Cell>
                        <strong>订单总额</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell style={{ textAlign: 'right' }}>
                        <strong style={{ fontSize: '16px', color: '#1890ff' }}>
                          ${normalizeMoney(selectedOrder.total_amount ?? '0')}
                        </strong>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  )}
                />
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Price Edit Modal */}
      <Modal
        title="编辑订单价格"
        open={priceModalVisible}
        onCancel={() => setPriceModalVisible(false)}
        onOk={() => priceForm.submit()}
        okText="确认"
        cancelText="取消"
        width={1200}
      >
        <Form
          form={priceForm}
          layout="vertical"
          onFinish={handleUpdatePrice}
          onValuesChange={calculateTotal}
        >
          {/* 商品价格编辑 */}
          <Divider>商品价格</Divider>
          <Form.List name="items_detail">
            {(fields) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Card key={key} size="small" style={{ marginBottom: 16 }}>
                    <Row gutter={16}>
                      <Col span={8}>
                        <div style={{ padding: '5px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {(() => {
                            const productId = priceForm.getFieldValue(['items_detail', name, 'product_id']);
                            const variantPath = priceForm.getFieldValue(['items_detail', name, 'variant_path']);
                            const product = products[productId];
                            const coverImageUrl = product ? getProductCoverImage(product) : null;
                            const variantImageUrl = product ? getVariantImage(product, variantPath) : null;
                            const formattedVariantPath = formatVariantPath(variantPath);

                            return (
                              <>
                                <div style={{ display: 'flex', justifyContent: 'center' }}>
                                  <Image
                                    src={variantImageUrl || coverImageUrl}
                                    alt={variantImageUrl ? "Variant image" : "Product cover"}
                                    width={40}
                                    height={40}
                                    style={{
                                      objectFit: 'cover',
                                      borderRadius: '4px',
                                      border: variantImageUrl ? '2px solid #ff6b35' : 'none',
                                      cursor: 'pointer'
                                    }}
                                    onClick={() => handleProductClick(productId)}
                                    onError={(e) => {
                                      // 如果变体图片加载失败，fallback到封面图片
                                      if (variantImageUrl && e.target.src === variantImageUrl) {
                                        e.target.src = coverImageUrl;
                                        e.target.style.border = 'none';
                                      } else {
                                        // 如果封面图片也失败，显示占位符
                                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjgiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5OL0E8L3RleHQ+PC9zdmc+';
                                        e.target.style.border = 'none';
                                      }
                                    }}
                                  />
                                </div>
                                <div>
                                  <div
                                    style={{
                                      fontWeight: 'bold',
                                      cursor: 'pointer',
                                      color: '#1890ff'
                                    }}
                                    onClick={() => handleProductClick(productId)}
                                    onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                    onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                  >
                                    {product ? product.name : `商品ID: ${productId}`}
                                  </div>
                                  <div style={{ fontSize: '12px', color: '#666' }}>
                                    数量: {priceForm.getFieldValue(['items_detail', name, 'quantity'])}
                                  </div>
                                  {formattedVariantPath && (
                                    <div style={{ fontSize: '12px', color: '#666' }}>
                                      规格: {formattedVariantPath}
                                    </div>
                                  )}
                                  {priceForm.getFieldValue(['items_detail', name, 'sku']) && (
                                    <div style={{ fontSize: '12px', color: '#1890ff', fontWeight: 'bold' }}>
                                      SKU: {priceForm.getFieldValue(['items_detail', name, 'sku'])}
                                    </div>
                                  )}
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          {...restField}
                          name={[name, 'unit_price']}
                          label="单价"
                          rules={[{ required: true, message: '请输入单价' }]}
                        >
                          <InputNumber
                            style={{ width: '100%' }}
                            precision={2}
                            min={0}
                            stringMode
                            addonBefore="$"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <div style={{ padding: '5px 0', marginTop: '30px' }}>
                          <Text strong>
                            小计: ${multiplyMoneyByQuantity(
                              priceForm.getFieldValue(['items_detail', name, 'unit_price']),
                              priceForm.getFieldValue(['items_detail', name, 'quantity'])
                            )}
                          </Text>
                        </div>
                      </Col>
                    </Row>
                  </Card>
                ))}
              </>
            )}
          </Form.List>

          {/* 价格详情编辑 */}
          <Divider>价格详情</Divider>
          <Form.List name="price_details">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Card key={key} size="small" style={{ marginBottom: 16 }}>
                    <Row gutter={16} align="middle">
                      <Col span={8}>
                        <Form.Item
                          {...restField}
                          name={[name, 'name']}
                          label="项目名称"
                          rules={[{ required: true, message: '请输入项目名称' }]}
                        >
                          <Input placeholder="如：运费、手续费等" />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item
                          {...restField}
                          name={[name, 'amount']}
                          label="金额"
                          rules={[{ required: true, message: '请输入金额' }]}
                        >
                          <InputNumber
                            style={{ width: '100%' }}
                            precision={2}
                            min={0}
                            stringMode
                            addonBefore="$"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item
                          {...restField}
                          name={[name, 'unit']}
                          label="单位"
                          initialValue="USD"
                        >
                          <Select>
                            <Option value="USD">USD</Option>
                            <Option value="CNY">CNY</Option>
                            <Option value="EUR">EUR</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Button
                          type="text"
                          danger
                          onClick={() => {
                            remove(name);
                            setTimeout(calculateTotal, 0);
                          }}
                          style={{ marginTop: '30px' }}
                        >
                          删除
                        </Button>
                      </Col>
                    </Row>
                  </Card>
                ))}
                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => {
                      add({ name: '', amount: '0.00', unit: 'USD' });
                      setTimeout(calculateTotal, 0);
                    }}
                    block
                    icon={<PlusOutlined />}
                  >
                    添加价格项目
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>

          {/* 总金额显示 */}
          <Divider>订单总额</Divider>
          <div style={{
            padding: '16px',
            backgroundColor: '#f5f5f5',
            borderRadius: '6px'
          }}>
            <div>
              <Row gutter={16}>
                <Col span={12}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ marginBottom: '4px' }}>
                      <Text style={{ fontSize: '12px', color: '#666' }}>
                        商品总价
                      </Text>
                    </div>
                    <Text strong style={{ fontSize: '18px', color: '#52c41a' }}>
                      ${priceSummary.itemsTotal}
                    </Text>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ marginBottom: '4px' }}>
                      <Text style={{ fontSize: '12px', color: '#666' }}>
                        其他费用
                      </Text>
                    </div>
                    <Text strong style={{ fontSize: '18px', color: '#fa8c16' }}>
                      ${priceSummary.priceDetailsTotal}
                    </Text>
                  </div>
                </Col>
              </Row>
              <Divider style={{ margin: '12px 0' }} />
              <div style={{ textAlign: 'center' }}>
                <div style={{ marginBottom: '4px' }}>
                  <Text style={{ fontSize: '14px', color: '#666' }}>
                    订单总额
                  </Text>
                </div>
                <Text strong style={{ fontSize: '24px', color: '#1890ff' }}>
                  ${priceSummary.orderTotal}
                </Text>
              </div>
            </div>
          </div>
        </Form>
      </Modal>

      {/* Fulfillment Management Modal */}
      <Modal
        title="管理订单状态"
        open={fulfillmentModalVisible}
        onCancel={() => setFulfillmentModalVisible(false)}
        onOk={() => fulfillmentForm.submit()}
        okText="确认更新"
        cancelText="取消"
        width={1200}
      >
        <Form
          form={fulfillmentForm}
          layout="vertical"
          onFinish={handleUpdateFulfillment}
        >
          {/* 批量操作区域 */}
          <Card size="small" style={{ marginBottom: 20, backgroundColor: '#f5f5f5' }}>
            <div style={{ marginBottom: 12 }}>
              <Text strong style={{ fontSize: '16px' }}>批量操作</Text>
              <Text style={{ marginLeft: 8, color: '#666', fontSize: '12px' }}>
                修改下方设置将应用到所有商品
              </Text>
            </div>
            <Row gutter={16}>
              <Col span={8}>
                <div style={{ marginBottom: 8 }}>
                  <Text strong>批量设置状态：</Text>
                </div>
                <Select
                  placeholder="选择状态应用到所有商品"
                  value={batchFulfillmentStatus}
                  onChange={handleBatchStatusChange}
                  style={{ width: '100%' }}
                  allowClear
                >
                  <Option value="AWAITING">等待确认</Option>
                  <Option value="ACCEPTED">已确认</Option>
                  <Option value="REJECTED">拒单</Option>
                  <Option value="ON_HOLD">缺货</Option>
                  <Option value="SHIPPED">已发货</Option>
                  <Option value="DELIVERED">已送达</Option>
                </Select>
              </Col>
              <Col span={8}>
                <div style={{ marginBottom: 8 }}>
                  <Text strong>批量设置物流公司：</Text>
                </div>
                <Select
                  placeholder="选择物流公司应用到所有商品"
                  value={batchShippingCompanyId}
                  onChange={handleBatchShippingCompanyChange}
                  style={{ width: '100%' }}
                  allowClear
                  optionFilterProp="label"
                  showSearch
                >
                  {shippingCompanies.map((company) => (
                    <Option key={company.id} value={company.id} label={company.name}>
                      {company.name}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col span={8}>
                <div style={{ marginBottom: 8 }}>
                  <Text strong>批量设置跟踪号：</Text>
                </div>
                <Input
                  placeholder="输入跟踪号应用到所有商品"
                  value={batchTrackingNumber}
                  onChange={(e) => handleBatchTrackingChange(e.target.value)}
                  allowClear
                />
              </Col>
            </Row>
            <Row justify="end" style={{ marginTop: 12 }}>
              <Col>
                <Space>
                  <Button onClick={handleBatchGenerateTrackingUrls}>
                    批量生成跟踪URL
                  </Button>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    需已选择物流公司并填写跟踪号
                  </Text>
                </Space>
              </Col>
            </Row>
          </Card>

          {/* 商品列表 */}
          <Divider>商品详情</Divider>
          <Form.List name="items">
            {(fields) => (
              <>
                {fields.map(({ key, name, ...restField }) => {
                  // 获取商品信息
                  const itemIndex = fulfillmentForm.getFieldValue(['items', name, 'item_index']);
                  const item = selectedOrder?.items_detail?.[itemIndex];
                  const product = item ? products[item.product_id] : null;
                  const coverImageUrl = product ? getProductCoverImage(product) : null;
                  const variantImageUrl = product ? getVariantImage(product, item?.variant_path) : null;

                  return (
                    <Card key={key} size="small" style={{ marginBottom: 16 }}>
                      <Row gutter={16}>
                        <Col span={8}>
                          {/* 商品信息 */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {/* 商品图片 */}
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                              <Image
                                src={variantImageUrl || coverImageUrl}
                                alt={variantImageUrl ? "Variant image" : "Product cover"}
                                width={50}
                                height={50}
                                style={{
                                  objectFit: 'cover',
                                  borderRadius: '4px',
                                  border: variantImageUrl ? '2px solid #ff6b35' : 'none',
                                  cursor: 'pointer'
                                }}
                                onClick={() => item && handleProductClick(item.product_id)}
                                onError={(e) => {
                                  // 如果变体图片加载失败，fallback到封面图片
                                  if (variantImageUrl && e.target.src === variantImageUrl) {
                                    e.target.src = coverImageUrl;
                                    e.target.style.border = 'none';
                                  } else {
                                    // 如果封面图片也失败，显示占位符
                                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTAiIGhlaWdodD0iNTAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjEwIiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+Tm8gSW1hZ2U8L3RleHQ+PC9zdmc+';
                                    e.target.style.border = 'none';
                                  }
                                }}
                              />
                            </div>
                            {/* 商品信息 */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div
                                style={{
                                  fontWeight: 'bold',
                                  cursor: 'pointer',
                                  color: '#1890ff',
                                  marginBottom: '4px',
                                  fontSize: '14px'
                                }}
                                onClick={() => item && handleProductClick(item.product_id)}
                                onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                              >
                                {product ? product.name : `商品ID: ${item?.product_id}`}
                              </div>
                              {item?.variant_path && item.variant_path.length > 0 && (
                                <div style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>
                                  规格: {item.variant_path.join(' - ')}
                                </div>
                              )}
                              {item?.sku && (
                                <div style={{ fontSize: '11px', color: '#1890ff', fontWeight: 'bold', marginBottom: '2px' }}>
                                  SKU: {item.sku}
                                </div>
                              )}
                              <div style={{ fontSize: '11px', color: '#666' }}>
                                数量: {item?.quantity} | 单价: ${normalizeMoney(item?.unit_price ?? '0')}
                              </div>
                            </div>
                          </div>
                        </Col>
                        <Col span={4}>
                          <Form.Item
                            {...restField}
                            name={[name, 'fulfillment_status']}
                            label="订单状态"
                            rules={[{ required: true, message: '请选择状态' }]}
                          >
                            <Select>
                              <Option value="AWAITING">等待确认</Option>
                              <Option value="ACCEPTED">已确认</Option>
                              <Option value="REJECTED">拒单</Option>
                              <Option value="ON_HOLD">缺货</Option>
                              <Option value="SHIPPED">已发货</Option>
                              <Option value="DELIVERED">已送达</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item
                            {...restField}
                            name={[name, 'shipping_company_id']}
                            label="物流公司"
                          >
                            <Select
                              placeholder="选择物流公司"
                              allowClear
                              optionFilterProp="label"
                              showSearch
                            >
                              {shippingCompanies.map((company) => (
                                <Option key={company.id} value={company.id} label={company.name}>
                                  {company.name}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item
                            {...restField}
                            name={[name, 'tracking_number']}
                            label="跟踪号"
                          >
                            <Input placeholder="发货时填写跟踪号" />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={24}>
                          <Form.Item label="跟踪URL">
                            <Input.Group compact>
                              <Form.Item
                                {...restField}
                                name={[name, 'tracking_url']}
                                noStyle
                              >
                                <Input
                                  placeholder="自定义或生成URL"
                                  style={{ width: 'calc(100% - 64px)' }}
                                />
                              </Form.Item>
                              <Button
                                size="small"
                                style={{ width: 64 }}
                                onClick={() => handleGenerateTrackingUrl(name)}
                              >
                                生成
                              </Button>
                            </Input.Group>
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={24}>
                          <Form.Item
                            {...restField}
                            name={[name, 'comment']}
                            label="备注"
                          >
                            <TextArea rows={2} placeholder="特殊状态时填写备注" />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                  );
                })}
              </>
            )}
          </Form.List>
        </Form>
      </Modal>

      {/* RMA Request Modal */}
      <Modal
        title="申请RMA售后"
        open={rmaModalVisible}
        onCancel={() => setRmaModalVisible(false)}
        onOk={() => rmaForm.submit()}
        okText="提交申请"
        cancelText="取消"
        width={1000}
      >
        <Form
          form={rmaForm}
          layout="vertical"
          onFinish={handleSubmitRMA}
        >
          <Form.List name="items">
            {(fields) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Card key={key} size="small" style={{ marginBottom: 16 }}>
                    <Row gutter={16}>
                      <Col span={4}>
                        <Form.Item
                          {...restField}
                          name={[name, 'selected']}
                          valuePropName="checked"
                        >
                          <Checkbox>选择</Checkbox>
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <div style={{ padding: '5px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {(() => {
                            const productId = rmaForm.getFieldValue(['items', name, 'product_id']);
                            const variantPath = rmaForm.getFieldValue(['items', name, 'variant_path']);
                            const product = products[productId];
                            const coverImageUrl = product ? getProductCoverImage(product) : null;
                            const variantImageUrl = product ? getVariantImage(product, variantPath) : null;
                            const formattedVariantPath = formatVariantPath(variantPath);

                            return (
                              <>
                                <div style={{ display: 'flex', justifyContent: 'center' }}>
                                  <Image
                                    src={variantImageUrl || coverImageUrl}
                                    alt={variantImageUrl ? "Variant image" : "Product cover"}
                                    width={40}
                                    height={40}
                                    style={{
                                      objectFit: 'cover',
                                      borderRadius: '4px',
                                      border: variantImageUrl ? '2px solid #ff6b35' : 'none',
                                      cursor: 'pointer'
                                    }}
                                    onClick={() => handleProductClick(productId)}
                                    onError={(e) => {
                                      // 如果变体图片加载失败，fallback到封面图片
                                      if (variantImageUrl && e.target.src === variantImageUrl) {
                                        e.target.src = coverImageUrl;
                                        e.target.style.border = 'none';
                                      } else {
                                        // 如果封面图片也失败，显示占位符
                                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjgiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5OL0E8L3RleHQ+PC9zdmc+';
                                        e.target.style.border = 'none';
                                      }
                                    }}
                                  />
                                </div>
                                <div>
                                  <div
                                    style={{
                                      fontWeight: 'bold',
                                      cursor: 'pointer',
                                      color: '#1890ff'
                                    }}
                                    onClick={() => handleProductClick(productId)}
                                    onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                    onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                  >
                                    {product ? product.name : `商品ID: ${productId}`}
                                  </div>
                                  <div style={{ fontSize: '12px', color: '#666' }}>
                                    数量: {rmaForm.getFieldValue(['items', name, 'quantity'])}
                                  </div>
                              {formattedVariantPath && (
                                <div style={{ fontSize: '12px', color: '#666' }}>
                                  规格: {formattedVariantPath}
                                </div>
                              )}
                                  {rmaForm.getFieldValue(['items', name, 'sku']) && (
                                    <div style={{ fontSize: '12px', color: '#1890ff', fontWeight: 'bold' }}>
                                      SKU: {rmaForm.getFieldValue(['items', name, 'sku'])}
                                    </div>
                                  )}
                                  <div style={{ fontSize: '12px', marginTop: 4 }}>
                                    <Tag color={getFulfillmentStatusColor(rmaForm.getFieldValue(['items', name, 'fulfillment_status']))}>
                                      {getFulfillmentStatusText(rmaForm.getFieldValue(['items', name, 'fulfillment_status']) || 'AWAITING')}
                                    </Tag>
                                  </div>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          {...restField}
                          name={[name, 'rma_comment']}
                          label="RMA原因"
                          rules={[
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                const selected = getFieldValue(['items', name, 'selected']);
                                if (selected && !value) {
                                  return Promise.reject(new Error('请填写选中商品的RMA原因'));
                                }
                                return Promise.resolve();
                              },
                            }),
                          ]}
                        >
                          <TextArea rows={2} placeholder="请描述申请售后的原因（如：质量问题、取消发货等）" />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                ))}
              </>
            )}
          </Form.List>
        </Form>
      </Modal>

      {/* RMA Update Modal */}
      <Modal
        title="更新RMA状态"
        open={rmaUpdateModalVisible}
        onCancel={() => setRmaUpdateModalVisible(false)}
        onOk={() => rmaUpdateForm.submit()}
        okText="确认更新"
        cancelText="取消"
        width={800}
      >
        <Form
          form={rmaUpdateForm}
          layout="vertical"
          onFinish={handleSubmitRMAUpdate}
        >
          <Form.List name="items">
            {(fields) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Card key={key} size="small" style={{ marginBottom: 16 }}>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          {...restField}
                          name={[name, 'rma_status']}
                          label="RMA状态"
                          rules={[{ required: true, message: '请选择RMA状态' }]}
                        >
                          <Select>
                            <Option value="RMA_REQUESTED">已申请售后</Option>
                            <Option value="RMA_AUTHORIZED">已同意售后</Option>
                            <Option value="RMA_IN_TRANSIT">商品已寄回</Option>
                            <Option value="RMA_RECEIVED">已收到寄回商品</Option>
                            <Option value="REFUND_COMPLETED">售后完毕</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          {...restField}
                          name={[name, 'rma_shipment']}
                          label="退货物流信息"
                        >
                          <Input placeholder="填写退货物流单号等信息" />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                ))}
              </>
            )}
          </Form.List>
        </Form>
      </Modal>

      {/* Message Edit Modal */}
      <Modal
        title="编辑订单备注"
        open={messageModalVisible}
        onCancel={() => setMessageModalVisible(false)}
        onOk={() => messageForm.submit()}
        okText="确认"
        cancelText="取消"
        width={600}
      >
        <Form
          form={messageForm}
          layout="vertical"
          onFinish={handleUpdateMessage}
        >
          <Form.Item
            label="订单备注"
            name="message"
          >
            <TextArea
              placeholder="输入订单的特殊说明或备注..."
              rows={4}
              maxLength={1000}
              showCount
            />
          </Form.Item>
          <div style={{
            padding: '8px 12px',
            backgroundColor: '#f0f8ff',
            borderRadius: '4px',
            fontSize: '12px',
            color: '#666'
          }}>
            <strong>说明：</strong> 管理员可以随时编辑订单备注。
          </div>
        </Form>
      </Modal>

      {/* Warning Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#faad14', fontSize: '18px' }}>⚠️</span>
            订单警告
          </div>
        }
        open={warningModalVisible}
        onCancel={() => setWarningModalVisible(false)}
        onOk={handleClearWarning}
        okText="确认"
        okButtonProps={{
          style: { backgroundColor: '#faad14', borderColor: '#faad14' },
          danger: true
        }}
        cancelText="取消"
        width={600}
      >
        <div style={{
          padding: '16px',
          backgroundColor: '#fff7e6',
          borderRadius: '6px',
          border: '1px solid #ffd591',
          marginBottom: '16px'
        }}>
          {selectedOrder?.warning && Array.isArray(selectedOrder.warning) && selectedOrder.warning.length > 0 ? (
            <div>
              {selectedOrder.warning.map((warning, index) => (
                <div key={index} style={{
                  marginBottom: index < selectedOrder.warning.length - 1 ? '12px' : '0',
                  paddingBottom: index < selectedOrder.warning.length - 1 ? '12px' : '0',
                  borderBottom: index < selectedOrder.warning.length - 1 ? '1px solid #ffd591' : 'none'
                }}>
                  <div style={{
                    fontSize: '12px',
                    color: '#666',
                    fontStyle: 'italic'
                  }}>
                    {new Date(warning.time * 1000).toLocaleString()}:
                  </div>
                  <div style={{
                    fontSize: '14px',
                    color: '#d46b08',
                    whiteSpace: 'pre-wrap',
                    lineHeight: '1.5',
                    marginBottom: '4px'
                  }}>
                    {warning.message}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{
              fontSize: '14px',
              color: '#d46b08',
              fontStyle: 'italic'
            }}>
              暂无警告信息
            </div>
          )}
        </div>
      </Modal>
    </div>
    </>
  );
};

export default OrderManagement;
